import { useEffect, useMemo, useState } from "react";
import type { FilmProject, Scene, StoryboardFrame } from "../types";
import { formatRuntime, sceneChunks } from "../utils/storyboard";
import { Icon } from "./Icon";

interface PromptPreviewModalProps {
  project: FilmProject;
  scene: Scene;
  frame: StoryboardFrame;
  onClose: () => void;
  onSavePrompt: (prompt: string, queueRegenerate?: boolean) => void;
}

function compactList(items: string[], max = 4): string {
  return items.filter(Boolean).slice(0, max).join("; ");
}

function referenceNotes(project: FilmProject, sceneId: string): string[] {
  return (project.uploadedAssets?.sceneReferences || [])
    .filter((asset) => asset.sceneId === sceneId && !asset.disabled)
    .flatMap((asset) => {
      const parsed = asset.parsedReference;
      if (!parsed) return [`${asset.label}: ${asset.fileName}`];
      return [
        parsed.storyWorld ? `${asset.label} world: ${parsed.storyWorld}` : "",
        parsed.location ? `location: ${parsed.location}` : "",
        parsed.designNotes.length ? `design: ${compactList(parsed.designNotes, 3)}` : "",
        parsed.cameraLens.length ? `camera/lens: ${compactList(parsed.cameraLens, 3)}` : "",
        parsed.lightingAtmosphere.length ? `lighting: ${compactList(parsed.lightingAtmosphere, 3)}` : "",
        parsed.colorPalette.length ? `palette: ${compactList(parsed.colorPalette, 5)}` : "",
        parsed.promptNotes.length ? `prompt notes: ${compactList(parsed.promptNotes, 4)}` : ""
      ].filter(Boolean);
    });
}

function buildPackagePrompt(project: FilmProject, scene: Scene, frame: StoryboardFrame, prompt: string): string {
  const chunk = sceneChunks(scene).find((item) => item.frameIds.includes(frame.id));
  const characterDetails = scene.characters.map((name) => {
    const character = project.characters.find((item) => item.name === name);
    if (!character) return name;
    const card = character.cardAssetLabel || character.cardSource || character.cardStatus;
    const voice = character.voiceStatus === "not_needed" ? "no voice" : character.voiceAssetLabel || character.voicePreset || character.voiceSource || character.voiceStatus;
    return `${name} (${character.role}; card: ${card}; voice: ${voice})`;
  });
  const refs = referenceNotes(project, scene.id);
  return [
    prompt.trim() || `${frame.shotType} for ${scene.slugline}`,
    `Scene: ${scene.slugline}. ${scene.synopsis}`,
    characterDetails.length ? `Characters: ${characterDetails.join("; ")}` : "",
    refs.length ? `Reference boards: ${refs.join(". ")}` : "",
    `Style: ${project.stylePreset}. Color grade: ${project.colorGrade}. Aspect: ${project.aspectRatio}. Output: ${project.resolution}.`,
    chunk ? `Timing: ${chunk.label}, ${formatRuntime(chunk.durationSeconds)}, frame IDs ${chunk.frameIds.join(", ")}.` : `Timing: ${formatRuntime(frame.durationSeconds)} storyboard frame.`,
    "Respect uploaded and locked source assets. Do not overwrite user-provided references."
  ].filter(Boolean).join("\n\n");
}

export function PromptPreviewModal({ project, scene, frame, onClose, onSavePrompt }: PromptPreviewModalProps) {
  const [prompt, setPrompt] = useState(frame.prompt || "");

  useEffect(() => {
    setPrompt(frame.prompt || "");
  }, [frame.id, frame.prompt]);

  const packagePrompt = useMemo(
    () => buildPackagePrompt(project, scene, frame, prompt),
    [frame, project, prompt, scene]
  );
  const chunk = sceneChunks(scene).find((item) => item.frameIds.includes(frame.id));
  const references = (project.uploadedAssets?.sceneReferences || []).filter((asset) => asset.sceneId === scene.id && !asset.disabled);
  const activeCharacters = project.characters.filter((character) => scene.characters.includes(character.name));

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="prompt-preview-modal" role="dialog" aria-modal="true" aria-label="Prompt preview and edit">
        <header>
          <div>
            <span>Prompt Preview</span>
            <h2>{frame.shotType}</h2>
            <p>{scene.slugline} · {chunk ? `${chunk.label} · ${formatRuntime(chunk.durationSeconds)}` : `${formatRuntime(frame.durationSeconds)}`}</p>
          </div>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close prompt preview">
            <Icon name="chevron" />
          </button>
        </header>

        <div className="prompt-preview-grid">
          <label className="prompt-editor-field">
            <span>Editable frame prompt</span>
            <textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="Describe exactly what this board should show..."
            />
          </label>

          <aside className="prompt-context-panel" aria-label="Prompt context">
            <div>
              <span>Scene</span>
              <strong>{scene.synopsis}</strong>
            </div>
            <div>
              <span>Characters</span>
              <strong>{activeCharacters.length ? activeCharacters.map((character) => character.name).join(", ") : "No visible characters"}</strong>
              <small>{activeCharacters.map((character) => character.voiceAssetLabel || character.voicePreset || character.voiceStatus).filter(Boolean).join(" · ")}</small>
            </div>
            <div>
              <span>References</span>
              <strong>{references.length ? `${references.length} attached` : "None attached"}</strong>
              <small>{references.map((asset) => asset.label).slice(0, 3).join(" · ")}</small>
            </div>
            <div>
              <span>Look</span>
              <strong>{project.stylePreset}</strong>
              <small>{project.colorGrade} · {project.aspectRatio} · {project.resolution}</small>
            </div>
          </aside>
        </div>

        <section className="package-prompt-preview" aria-label="Package prompt preview">
          <div>
            <span>EvoLink / storyboard package prompt</span>
            <strong>Assembled from frame, scene, sources, style, and chunk timing</strong>
          </div>
          <pre>{packagePrompt}</pre>
        </section>

        <footer className="modal-actions">
          <button className="secondary-action" onClick={onClose} type="button">Cancel</button>
          <button className="secondary-action" onClick={() => onSavePrompt(prompt, false)} type="button">Save Prompt</button>
          <button className="primary-action" onClick={() => onSavePrompt(prompt, true)} type="button">
            <Icon name="spark" />
            Save & Queue
          </button>
        </footer>
      </section>
    </div>
  );
}
